import React from 'react';
import {
  Paper,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Box,
  InputAdornment,
  Divider,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  ProblemCategory,
  ProblemTag,
  PoolProblemFilters
} from '../../../../services/problemPoolService';

interface ProblemPoolFiltersProps {
  categories: ProblemCategory[];
  tags: ProblemTag[];
  filters: PoolProblemFilters;
  onFiltersChange: (filters: PoolProblemFilters) => void;
}

const difficultyLevels = [
  { value: 1, label: '★ Easy' },
  { value: 2, label: '★★ Medium' },
  { value: 3, label: '★★★ Challenging' },
  { value: 4, label: '★★★★ Hard' },
  { value: 5, label: '★★★★★ Expert' }
];

const ProblemPoolFilters: React.FC<ProblemPoolFiltersProps> = ({
  categories,
  tags,
  filters,
  onFiltersChange
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: e.target.value || undefined });
  };

  const handleCategoryChange = (categoryId: number) => {
    if (filters.categoryId === categoryId) {
      onFiltersChange({ ...filters, categoryId: undefined });
    } else {
      onFiltersChange({ ...filters, categoryId });
    }
  };

  const handleDifficultyChange = (level: number) => {
    const currentLevels = filters.difficultyLevels || [];
    if (currentLevels.includes(level)) {
      onFiltersChange({
        ...filters,
        difficultyLevels: currentLevels.filter(l => l !== level)
      });
    } else {
      onFiltersChange({
        ...filters,
        difficultyLevels: [...currentLevels, level]
      });
    }
  };

  const handleTagToggle = (tagId: number) => {
    const currentTags = filters.tagIds || [];
    if (currentTags.includes(tagId)) {
      onFiltersChange({
        ...filters,
        tagIds: currentTags.filter(t => t !== tagId)
      });
    } else {
      onFiltersChange({
        ...filters,
        tagIds: [...currentTags, tagId]
      });
    }
  };

  const handleProblemTypeChange = (type: 'open_ended' | 'multiple_choice' | undefined) => {
    if (filters.problemType === type) {
      onFiltersChange({ ...filters, problemType: undefined });
    } else {
      onFiltersChange({ ...filters, problemType: type });
    }
  };

  const handleClearFilters = () => {
    onFiltersChange({
      sortBy: 'created_at',
      sortOrder: 'desc',
      page: 1,
      limit: 20
    });
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.categoryId ||
    filters.difficultyLevels?.length ||
    filters.tagIds?.length ||
    filters.problemType
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        )}
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search problems..."
        value={filters.search || ''}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 2 }} />

      {/* Problem Type */}
      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Problem Type</Typography>
        </FormLabel>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.problemType === 'open_ended'}
                onChange={() => handleProblemTypeChange('open_ended')}
                size="small"
              />
            }
            label="Open Ended"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.problemType === 'multiple_choice'}
                onChange={() => handleProblemTypeChange('multiple_choice')}
                size="small"
              />
            }
            label="Multiple Choice"
          />
        </FormGroup>
      </FormControl>

      <Divider sx={{ mb: 2 }} />

      {/* Categories */}
      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Category</Typography>
        </FormLabel>
        <FormGroup>
          {categories.map(category => (
            <FormControlLabel
              key={category.id}
              control={
                <Checkbox
                  checked={filters.categoryId === category.id}
                  onChange={() => handleCategoryChange(category.id)}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: category.color
                    }}
                  />
                  <span>{category.name}</span>
                  {category.problemCount !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      ({category.problemCount})
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </FormGroup>
      </FormControl>

      <Divider sx={{ mb: 2 }} />

      {/* Difficulty */}
      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Difficulty</Typography>
        </FormLabel>
        <FormGroup>
          {difficultyLevels.map(level => (
            <FormControlLabel
              key={level.value}
              control={
                <Checkbox
                  checked={filters.difficultyLevels?.includes(level.value) || false}
                  onChange={() => handleDifficultyChange(level.value)}
                  size="small"
                />
              }
              label={level.label}
            />
          ))}
        </FormGroup>
      </FormControl>

      <Divider sx={{ mb: 2 }} />

      {/* Tags */}
      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Tags</Typography>
        </FormLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {tags.map(tag => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              variant={filters.tagIds?.includes(tag.id) ? 'filled' : 'outlined'}
              onClick={() => handleTagToggle(tag.id)}
              sx={{
                bgcolor: filters.tagIds?.includes(tag.id) ? tag.color : 'transparent',
                color: filters.tagIds?.includes(tag.id) ? 'white' : 'inherit',
                borderColor: tag.color,
                '&:hover': {
                  bgcolor: filters.tagIds?.includes(tag.id) ? tag.color : `${tag.color}20`
                }
              }}
            />
          ))}
        </Box>
      </FormControl>
    </Paper>
  );
};

export default ProblemPoolFilters;
